import { useEffect, useMemo, useState } from 'react'
import { Button, InfoTip } from '@alwindows/shared'
import {
  getScreenColours,
  getScreenImageSrc,
  getScreenOptions,
  type ProdFam,
  type ScreenColour,
  type ScreenOption,
  type ScreenSubOption,
} from '@alwindows/shared/api/screens'
import {
  getConfigCharacteristic,
  getConfigValue,
  lineTotal,
  prodFamFromConfig,
  RETRACTABLE_DOOR,
  type ScreenLine,
} from '../quote/types'
import { colourBaseName, filterColoursForState } from '../quote/colourAvailability'
import { STUB_COLOURS, STUB_OPTIONS, STUB_SO } from './_stubs'

/**
 * Inline configurator for a single screen line. Used in two places:
 *   1. Inside a Select screens row when the user clicks "Customise" — the
 *      configuration lives next to the product it belongs to.
 *   2. On the standalone `/edit/:sono/:itemNo` route (deep-link support).
 *
 * The screen-type choice is the dominant decision: each option is a card
 * that expands open when selected, revealing only the sub-options that
 * apply to it (mesh / grill / pet flap / triple lock). Frame colour and
 * a live running total live below.
 */

// Colorbond/A&L colour-code → hex. The SAP `ColourCode` field returns codes
// like 'WH' / 'DU' — not CSS colours — so the swatch needs a lookup.
const COLORBOND_HEX: Record<string, string> = {
  WH: '#f3f1ee',
  DU: '#d6cdb8',
  PB: '#cfc5b4',
  CL: '#e9dfc2',
  WG: '#5b5e58',
  MO: '#3d3d3d',
  BL: '#1a1a1a',
}

// Legacy hardcoded ProdType gates (alwindows-ecommerce Editor.js). SAP's
// per-option availability flags are unmaintained, so legacy keyed these
// behaviours off ProdType instead — we replicate that.
const BARRIER_DOOR = 'BR' // triple lock + pet flap are barrier-door-only
const MARINE_GRADE_GRILLE = 'SS' // integral mesh — the separate mesh picker doesn't apply
// RETRACTABLE_DOOR ('RET', no frame-colour choice) is imported from
// quote/types — the Summary needs the same gate when listing the colour.

// Steel mesh is only sold on BAL40 jobs — below that it's a product A&L
// doesn't want to sell or recommend (client request, Aug 2026). SAP trims the
// mesh list by BAL but deliberately keeps SSMH at every rating (live tenant:
// bal '12.5' → [ALMH, SSMH], '40' → [SSMH]), so the frontend gates it — same
// hardcoded-ProdType approach as the gates above.
const STEEL_MESH = 'SSMH'
// The live BAL_RATING characteristic is a raw string — 'LOW', '12.5', '40',
// 'NOT SUPPLIED' — not the BalRating union's 'BAL40' spelling (that spelling
// returns zero options from /screen/options). Accept both, plus flame zone
// ('FZ', above BAL40), so the gate can't mis-hide on an unexpected format.
function steelMeshAllowed(balRating: string): boolean {
  return /40|FZ/i.test(balRating)
}

/** Drop the steel-mesh row from an option's mesh list on sub-BAL40 jobs. */
function gateSteelMesh(option: ScreenOption, balRating: string): ScreenOption {
  if (steelMeshAllowed(balRating)) return option
  return {
    ...option,
    ScreenToMesh: {
      results: option.ScreenToMesh.results.filter((m) => m.ProdType !== STEEL_MESH),
    },
  }
}

/**
 * The sub-options a customer can actually pick. SAP pads these lists with
 * placeholder rows: some fully empty (an empty ProdType also matches the
 * "nothing picked yet" value, painting a blank row as selected), some hollow
 * (a ProdType but no Desc). Everything that decides whether a group EXISTS
 * has to agree with what the group RENDERS — otherwise the completeness gate
 * below could demand a choice from a group with nothing in it.
 */
function pickable(results: ScreenSubOption[]): ScreenSubOption[] {
  return results.filter((o) => o.ProdType && o.Desc)
}

/**
 * Which of the choices on offer still need making, in the order they appear.
 * Mirrors legacy `Editor.isComplete` (alwindows-ecommerce): the screen type,
 * a grill when the type offers one, a mesh when it offers one — unless the
 * marine-grade grille was chosen, whose mesh is integral — and a frame
 * colour. Pet flap and triple lock are extras: legacy's pet-flap branch sat
 * behind an `else if` that could never be reached, so it never required one,
 * and neither do we.
 *
 * Returns labels rather than a boolean so the footer can say what's missing
 * instead of just greying Done out.
 */
function missingChoices(
  draft: ScreenLine,
  option: ScreenOption | null,
  colourPickerShown: boolean,
): string[] {
  if (!option) return ['a screen type']
  const missing: string[] = []
  if (option.IsGrillType && pickable(option.ScreenToGrill.results).length > 0 && !draft.grillType)
    missing.push('a grill')
  if (
    option.IsMeshType &&
    pickable(option.ScreenToMesh.results).length > 0 &&
    draft.grillType !== MARINE_GRADE_GRILLE &&
    !draft.meshType
  )
    missing.push('an insect mesh')
  if (colourPickerShown && !draft.frameColour) missing.push('a frame colour')
  return missing
}

export type LineConfiguratorProps = {
  line: ScreenLine
  salesOrg: string
  /**
   * Installation state ("QLD"). SAP returns the same colour list for every
   * sales org with no usable regional data, so the picker hides colours A&L
   * doesn't sell in this state using the table in quote/colourAvailability
   * (from the site's "Colour options" page). Omit/blank hides nothing.
   */
  state?: string
  onSave: (next: ScreenLine) => void
  /**
   * The user backed out. In draft mode nothing was committed, so the host
   * just closes. In live mode the host is responsible for restoring the line
   * to how it stood when the panel opened — the picks are already in the
   * quote, so "Cancel" only means anything if someone rolls them back.
   */
  onCancel: () => void
  /**
   * Live mode: when provided, every draft change that has a screen type
   * picked is committed immediately (with prices baked in) instead of
   * waiting for Save. The footer becomes "Cancel / Done". Used by the Select
   * screens page so its fixed cart bar tracks the user's choices as they
   * make them. The /edit deep-link keeps draft+Save.
   */
  onChange?: (next: ScreenLine) => void
  /**
   * Live mode: the user accepted with Done. Everything is already in the
   * quote, so the host only has to close the panel. Falls back to onCancel
   * for callers that don't separate the two.
   */
  onDone?: () => void
  /**
   * The home's existing frame colour NAME ("White"), taken from the first
   * item's FM_ALU_COLOUR Value — the order is a single colour (legacy seeded
   * one `defaultFrame` for the whole SO from `results[0]`). SAP only reliably
   * carries the colour Value on the first item, so later windows/doors have
   * no per-item colour to match; this lets every line fall back to the home
   * colour so its swatch pre-selects, not just the first one.
   */
  homeColourName?: string
}

export function LineConfigurator({
  line,
  salesOrg,
  state = '',
  onSave,
  onCancel,
  onChange,
  onDone,
  homeColourName = '',
}: LineConfiguratorProps) {
  const [colours, setColours] = useState<ScreenColour[]>([])
  const [options, setOptions] = useState<ScreenOption[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState<ScreenLine>({ ...line })

  useEffect(() => {
    // The home's existing colour must arrive pre-selected. The line seeds
    // frameColour from the item's FM_ALU_COLOUR CHARACTERISTIC, but
    // /v1/screen/colours swatches key on their own ColourCode — SAP doesn't
    // guarantee the two schemes match. When they don't, fall back the way
    // legacy did: match the characteristic's human Value ("White") against
    // the colour descriptions and adopt that swatch's code.
    const alignFrameColour = (cols: ScreenColour[]) => {
      // Prefer this line's own colour Value, but fall back to the home colour
      // — SAP only puts FM_ALU_COLOUR's Value on the first item, so without
      // this every window after the first matches nothing and shows no
      // pre-selected swatch.
      // SAP's swatch Desc can carry a suffix the order's colour Value
      // doesn't, e.g. Desc "Jasper (VIC Only)" vs FM_ALU_COLOUR Value
      // "Jasper". Compare on the same base name the state table keys on so
      // the home colour still pre-selects instead of matching nothing.
      const valueName = colourBaseName(
        getConfigValue(line.sourceConfig, 'FM_ALU_COLOUR') || homeColourName,
      )
      setDraft((d) => {
        if (cols.some((c) => c.ColourCode === d.frameColour)) return d
        const match = valueName ? cols.find((c) => colourBaseName(c.Desc) === valueName) : undefined
        return match ? { ...d, frameColour: match.ColourCode } : d
      })
    }

    // Stub branch — short-circuit SAP calls when configuring a stub item so
    // the user can see the full configurator UI even when the non-prod
    // tenant has no options/colours for the test SO.
    if (line.sourceItem.SONo === STUB_SO) {
      const cols = filterColoursForState(STUB_COLOURS, state)
      setColours(cols)
      alignFrameColour(cols)
      setOptions([...STUB_OPTIONS].sort((a, b) => a.Sortorder - b.Sortorder))
      setLoading(false)
      return
    }
    let cancelled = false
    void (async () => {
      setLoading(true)
      setError(null)
      try {
        const prodFam: ProdFam = prodFamFromConfig(line.sourceConfig)
        const balRating =
          line.balRating || getConfigCharacteristic(line.sourceConfig, 'BAL_RATING') || 'LOW'
        const [allColours, opts] = await Promise.all([
          getScreenColours(salesOrg),
          getScreenOptions({
            prodFam,
            balRating,
            salesOrg,
            soNo: line.sourceItem.SONo,
            itemNo: line.sourceItem.ItemNo,
          }),
        ])
        if (!cancelled) {
          // Drop colours A&L doesn't sell in this state (e.g. Jasper on a
          // QLD job, Shale Grey on a VIC job) BEFORE anything downstream sees
          // the list, so the swatches, the completeness gate, and the
          // home-colour pre-select all agree on what can actually be ordered.
          const cols = filterColoursForState(allColours, state)
          setColours(cols)
          alignFrameColour(cols)
          // SAP pads option lists with placeholder rows: some fully empty (no
          // ProdType — legacy filtered these too), some hollow (a ProdType but
          // no Desc/Currency/pricing, e.g. an option SAP can't price for this
          // item). Neither is selectable, so require a name as well.
          setOptions(
            opts
              .filter((o) => o.ProdType && o.Desc)
              .map((o) => gateSteelMesh(o, balRating))
              .sort((a, b) => a.Sortorder - b.Sortorder),
          )
          // A quote persisted before the gate existed can carry a steel-mesh
          // pick on a sub-BAL40 job. The pill it points at no longer renders,
          // so clear it — the completeness gate then asks for a mesh again
          // instead of silently keeping a choice the customer can't see.
          if (!steelMeshAllowed(balRating)) {
            setDraft((d) => (d.meshType === STEEL_MESH ? { ...d, meshType: '' } : d))
          }
        }
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Failed to load configuration options.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
    // Re-fetch ONLY when the item, sales org or customer state changes (the
    // state is a primitive fixed at the address step). Everything the fetch
    // depends on (prodFam, balRating, the frame-colour alignment inputs) is
    // derived from the item's config, which is a frozen snapshot tied to
    // SONo + ItemNo — so those primitives fully identify what to load and are
    // referentially stable across edits.
    //
    // Previously this keyed on the `line.sourceConfig` ARRAY (plus other
    // line-derived values). In live mode the Select-screens page re-supplies
    // the line on every edit, and any path that hands back an equivalent-but-
    // new config reference (a rebuild from the persisted quote) re-ran the
    // whole colours+options fetch — flashing "Loading options…" over the
    // user's in-progress mesh/grill selection. Keying on the stable item
    // identity makes the configurator immune to that reference churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line.sourceItem.SONo, line.sourceItem.ItemNo, salesOrg, state])

  const pickedOption = useMemo(
    () => options.find((o) => o.ProdType === draft.screenOption) ?? null,
    [draft.screenOption, options],
  )

  // Bake every price the chosen configuration carries onto the line — the
  // option's own prices AND the picked mesh/grill/pet-flap sub-prices.
  // Committing, saving, and the running-total display all go through this
  // one function so the cart total can never disagree with the configurator.
  const priceLine = (next: ScreenLine): ScreenLine | null => {
    const opt = options.find((o) => o.ProdType === next.screenOption)
    if (!opt) return null
    const num = (v: string | undefined) => parseFloat(v ?? '0') || 0
    const sub = (results: ScreenSubOption[], picked: string) =>
      results.find((r) => r.ProdType === picked)
    // A marine-grade grille includes its mesh, so any mesh selection is
    // disregarded for pricing (the picker is disabled in the UI, but guard
    // here too so the price is correct regardless of stale state).
    const mesh =
      next.grillType === MARINE_GRADE_GRILLE ? undefined : sub(opt.ScreenToMesh.results, next.meshType)
    const grill = sub(opt.ScreenToGrill.results, next.grillType)
    const petFlap = sub(opt.ScreenToPetFlap.results, next.petFlapType)
    return {
      ...next,
      // Qty is owned by the surrounding surface — use the latest from `line`
      // so an edit to qty during configuration isn't clobbered on save.
      qty: line.qty,
      unitPrice: num(opt.Price),
      fitCost: num(opt.FitCost),
      tripleLockCost: num(opt.TripleLockCost),
      nonTradeCharge: num(opt.NonTradeCharge),
      meshPrice: num(mesh?.Price),
      meshNonTrade: num(mesh?.NonTradeCharge),
      grillPrice: num(grill?.Price),
      grillNonTrade: num(grill?.NonTradeCharge),
      petFlapPrice: num(petFlap?.Price),
      petFlapNonTrade: num(petFlap?.NonTradeCharge),
      screenOptionDesc: opt.Desc,
      meshDesc: mesh?.Desc ?? '',
      grillDesc: grill?.Desc ?? '',
      petFlapDesc: petFlap?.Desc ?? '',
      // Cache each sub-option's ProdFam for the SAP ToMesh/ToGrill/ToPetflap
      // payload — legacy sent the option row's own ProdFam.
      meshProdFam: mesh?.ProdFam ?? '',
      grillProdFam: grill?.ProdFam ?? '',
      petFlapProdFam: petFlap?.ProdFam ?? '',
      frameColourDesc:
        colours.find((c) => c.ColourCode === next.frameColour)?.Desc ?? next.frameColourDesc ?? '',
    }
  }

  // Single funnel for draft edits. In live mode (onChange present) any
  // change made after a screen type is picked also commits to the quote
  // with all prices baked in, so the host's totals stay in step.
  const updateDraft = (next: ScreenLine) => {
    setDraft(next)
    if (!onChange) return
    const priced = priceLine(next)
    if (priced) onChange(priced)
  }

  // One screen type on offer is not a choice — pre-select it so the customer
  // lands straight on the sub-options that actually vary, instead of clicking
  // the only card on the page first. Goes through updateDraft, so in live
  // mode it commits exactly as a click would and the cart bar stays honest.
  useEffect(() => {
    if (loading || options.length !== 1 || draft.screenOption) return
    updateDraft({ ...draft, screenOption: options[0]!.ProdType })
    // Runs once per option load: the guard above is false the moment a screen
    // type exists, so re-running on `draft` identity can't loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, loading, draft.screenOption])

  // Live running total — same lineTotal() the cart bar sums, applied to the
  // draft as if it were committed right now.
  const runningTotal = useMemo(() => {
    const priced = priceLine(draft)
    return priced ? lineTotal(priced) : 0
    // priceLine reads `options` and `line.qty`, both stable per fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, options, line.qty])

  // What still has to be picked before this line is a valid order line. The
  // colour picker is only a requirement when it's actually on screen —
  // retractables don't get one, so they must not be gated on it.
  const missing = useMemo(
    () =>
      missingChoices(
        draft,
        pickedOption,
        colours.length > 0 && draft.screenOption !== RETRACTABLE_DOOR,
      ),
    [draft, pickedOption, colours.length],
  )

  const handleSave = () => {
    const priced = priceLine(draft)
    if (!priced) {
      setError('Please choose a screen type.')
      return
    }
    onSave(priced)
  }

  if (loading) {
    return <p className="al-cfg__loading dark-grey xsmall">Loading options…</p>
  }

  if (error) {
    return (
      <div className="al-cfg">
        <p role="alert" className="al-cfg__error accent xsmall">
          {error}
        </p>
        <div className="al-cfg__actions">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    )
  }

  if (options.length === 0) {
    // Audit gap: legacy /Editor.js handled `noResults: true` when SAP
    // returned no compatible screen options for this item.
    return (
      <div className="al-cfg">
        <p className="al-cfg__error xsmall">
          We couldn&rsquo;t find any screen options that fit this item&rsquo;s product family or BAL
          rating. Please contact our team on <strong>1800 ALWINDOWS</strong> for help.
        </p>
        <div className="al-cfg__actions">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="al-cfg">
      <section aria-labelledby={`al-cfg-type-${line.sourceItem.SONo}-${line.sourceItem.ItemNo}`}>
        <h3
          id={`al-cfg-type-${line.sourceItem.SONo}-${line.sourceItem.ItemNo}`}
          className="medium bold no-m"
        >
          Screen type
        </h3>
        <p className="xsmall dark-grey m-bx3">
          Pick a screen. The options that fit will open up underneath your choice.
        </p>
        <div
          className="al-cfg__types"
          role="radiogroup"
          aria-labelledby={`al-cfg-type-${line.sourceItem.SONo}-${line.sourceItem.ItemNo}`}
        >
          {options.map((opt) => (
            <ScreenTypeCard
              key={opt.ProdType}
              option={opt}
              active={draft.screenOption === opt.ProdType}
              draft={draft}
              onSelect={() =>
                updateDraft({
                  ...draft,
                  screenOption: opt.ProdType,
                  meshType: '',
                  grillType: '',
                  petFlapType: '',
                  hasTripleLock: false,
                })
              }
              onDraftChange={updateDraft}
            />
          ))}
        </div>
      </section>

      {/* Retractable insect screens have no frame-colour choice — legacy hid
          the picker for them (Editor.js renderFrameColourPicker). */}
      {colours.length > 0 && draft.screenOption !== RETRACTABLE_DOOR ? (
        <section
          className="al-cfg__colour"
          aria-labelledby={`al-cfg-colour-${line.sourceItem.SONo}-${line.sourceItem.ItemNo}`}
        >
          <h3
            id={`al-cfg-colour-${line.sourceItem.SONo}-${line.sourceItem.ItemNo}`}
            className="small bold no-m-t m-bx1"
          >
            Frame colour
          </h3>
          <p className="xsmall dark-grey m-bx2">
            {colours.find((c) => c.ColourCode === draft.frameColour)?.Desc ?? 'Choose a colour'}
          </p>
          <div
            className="al-cfg__swatches"
            role="radiogroup"
            aria-labelledby={`al-cfg-colour-${line.sourceItem.SONo}-${line.sourceItem.ItemNo}`}
          >
            {colours.map((c) => (
              <button
                key={c.ColourCode}
                type="button"
                role="radio"
                aria-checked={draft.frameColour === c.ColourCode}
                aria-label={c.Desc}
                title={c.Desc}
                className={`al-cfg__swatch ${draft.frameColour === c.ColourCode ? 'al-cfg__swatch--active' : ''}`}
                style={{ background: COLORBOND_HEX[c.ColourCode] ?? c.ColourCode }}
                onClick={() => updateDraft({ ...draft, frameColour: c.ColourCode })}
              />
            ))}
          </div>
        </section>
      ) : null}

      <footer className="al-cfg__footer">
        <div className="al-cfg__total">
          <p className="xxsmall uppercase dark-grey no-m">Subtotal</p>
          <p className="large bold no-m al-cfg__total-amount">${runningTotal.toFixed(2)}</p>
        </div>
        <div className="al-cfg__actions">
          {/* A disabled button with no explanation reads as a broken one —
              name the choice that's still outstanding. */}
          {missing.length > 0 ? (
            <p className="al-cfg__missing xsmall dark-grey no-m" role="status">
              Choose {missing.join(' and ')} to finish.
            </p>
          ) : null}
          {onChange ? (
            // Live mode — Done keeps what's already committed and closes;
            // Cancel asks the host to put the line back as it was.
            <>
              <Button variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button variant="primary" disabled={missing.length > 0} onClick={onDone ?? onCancel}>
                Done
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button variant="primary" disabled={missing.length > 0} onClick={handleSave}>
                Save
              </Button>
            </>
          )}
        </div>
      </footer>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Expanding screen-type card. Visually anchors the dependent options (mesh,
// grill, pet flap, triple lock) inside the active card so the user never has
// to scroll to find what their choice unlocked.
// ---------------------------------------------------------------------------
function ScreenTypeCard({
  option,
  active,
  draft,
  onSelect,
  onDraftChange,
}: {
  option: ScreenOption
  active: boolean
  draft: ScreenLine
  onSelect: () => void
  onDraftChange: (next: ScreenLine) => void
}) {
  const tripleLockCost = parseFloat(option.TripleLockCost || '0')
  // The card's headline price must match the base the subtotal actually
  // charges. lineTotal() sums Price + FitCost + NonTradeCharge for the base
  // (legacy Editor.js rendered the same three terms); showing Price alone
  // under-reports the option — e.g. a $37.40 Price on a screen whose real
  // base is $55.00 once FitCost + NonTradeCharge are added.
  const basePrice =
    parseFloat(option.Price || '0') +
    parseFloat(option.FitCost || '0') +
    parseFloat(option.NonTradeCharge || '0')
  const hasMesh = option.IsMeshType && pickable(option.ScreenToMesh.results).length > 0
  const hasGrill = option.IsGrillType && pickable(option.ScreenToGrill.results).length > 0
  // Pet flap is a barrier-door-only extra. Like triple lock, SAP's IsPetFlap
  // flag isn't reliable, so legacy gated it on the barrier-door ProdType
  // rather than trusting the flag.
  const hasPetFlap =
    option.ProdType === BARRIER_DOOR &&
    option.IsPetFlap &&
    pickable(option.ScreenToPetFlap.results).length > 0
  // Triple lock is a barrier-door-only extra. SAP returns a nonzero
  // TripleLockCost on the fly screen door too, and its HasTripleLock flag is
  // unmaintained (false on every door), so legacy never trusted either —
  // it gated visibility purely on the barrier-door ProdType. Match that:
  // cost alone would wrongly surface the option on the fly screen door.
  const hasTripleLock = option.ProdType === BARRIER_DOOR && tripleLockCost > 0
  // Marine-grade grille ('SS', "Home Style — no grille") has integral
  // stainless mesh, so the separate mesh picker doesn't apply. Legacy hid it;
  // per product decision we keep it visible but disabled so its absence is
  // explained rather than mysterious.
  const meshDisabled = draft.grillType === MARINE_GRADE_GRILLE
  const hasExtras = hasMesh || hasGrill || hasPetFlap || hasTripleLock
  // When ANY sub-option group in this card has product images, every group
  // reserves the preview column so option rows align vertically across
  // groups instead of some stretching full-width.
  const reservePreview = [
    ...option.ScreenToMesh.results,
    ...option.ScreenToGrill.results,
    ...option.ScreenToPetFlap.results,
  ].some((o) => o.Image)

  return (
    <label className={`al-cfg__type-card ${active ? 'is-active' : ''}`}>
      <input
        type="radio"
        name={`screen-type-${draft.sourceItem.SONo}-${draft.sourceItem.ItemNo}`}
        value={option.ProdType}
        checked={active}
        onChange={onSelect}
        className="al-cfg__type-card-input"
      />
      <div className="al-cfg__type-card-head">
        <span className="al-cfg__type-card-radio" aria-hidden="true" />
        <div className="al-cfg__type-card-text">
          <p className="small bold no-m">{option.Desc}</p>
        </div>
        <p className="al-cfg__type-card-price small bold no-m">
          ${basePrice.toFixed(2)}
        </p>
        {/* InfoText lives behind the ⓘ (live-site parity) rather than as an
            always-visible description line under the title. */}
        <InfoTip text={option.InfoText} label={`About ${option.Desc}`} />
      </div>

      {hasExtras ? (
        <div className="al-cfg__type-card-body" aria-hidden={!active}>
          <div className="al-cfg__type-card-body-inner">
            {hasMesh ? (
              <OptionPills
                title="Insect mesh"
                name={`mesh-${draft.sourceItem.SONo}-${draft.sourceItem.ItemNo}-${option.ProdType}`}
                options={option.ScreenToMesh.results}
                value={meshDisabled ? '' : draft.meshType}
                onChange={(v) => onDraftChange({ ...draft, meshType: v })}
                reservePreview={reservePreview}
                disabled={meshDisabled}
                disabledNote="Included with the marine-grade grille"
              />
            ) : null}
            {hasGrill ? (
              <OptionPills
                title="Grill"
                name={`grill-${draft.sourceItem.SONo}-${draft.sourceItem.ItemNo}-${option.ProdType}`}
                options={option.ScreenToGrill.results}
                value={draft.grillType}
                // Selecting the marine-grade grille clears any mesh choice —
                // its mesh is integral, so a separate mesh must not be charged.
                onChange={(v) =>
                  onDraftChange({
                    ...draft,
                    grillType: v,
                    meshType: v === MARINE_GRADE_GRILLE ? '' : draft.meshType,
                  })
                }
                reservePreview={reservePreview}
              />
            ) : null}
            {hasPetFlap ? (
              <OptionPills
                title="Pet flap"
                name={`pet-${draft.sourceItem.SONo}-${draft.sourceItem.ItemNo}-${option.ProdType}`}
                options={option.ScreenToPetFlap.results}
                value={draft.petFlapType}
                onChange={(v) => onDraftChange({ ...draft, petFlapType: v })}
                reservePreview={reservePreview}
              />
            ) : null}
            {hasTripleLock ? (
              <div className="al-cfg__lock-row">
                {/* Themed checkbox pattern — real input offscreen, the visible
                    faux box is rendered as ::after on the adjacent label (per
                    the WP theme's `.input-checkbox + label` rule). Matches
                    how spare-parts CatalogFilters and PricingCalc render
                    checkboxes — single source of truth for the visual. */}
                <input
                  id={`triple-lock-${draft.sourceItem.SONo}-${draft.sourceItem.ItemNo}`}
                  type="checkbox"
                  className="input-checkbox"
                  checked={draft.hasTripleLock}
                  onChange={(e) => onDraftChange({ ...draft, hasTripleLock: e.target.checked })}
                />
                <label
                  htmlFor={`triple-lock-${draft.sourceItem.SONo}-${draft.sourceItem.ItemNo}`}
                  className="al-cfg__lock"
                >
                  <span className="al-cfg__lock-text">
                    <span className="bold xsmall">Triple lock</span>
                    <span className="xsmall dark-grey">Extra security on the active panel</span>
                  </span>
                  <span className="al-cfg__lock-price accent bold xsmall">
                    +${tripleLockCost.toFixed(2)}
                  </span>
                </label>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </label>
  )
}

// Vertically stacked option rows with a large image preview alongside.
// The preview shows the SELECTED option's product art in detail; before
// anything is picked it hints with the first option's image, dimmed, so the
// panel doesn't sit empty.
function OptionPills({
  title,
  name,
  options,
  value,
  onChange,
  reservePreview = false,
  disabled = false,
  disabledNote,
}: {
  title: string
  name: string
  options: ScreenSubOption[]
  value: string
  onChange: (v: string) => void
  /**
   * Keep the preview column's width even when THIS group has no images, so
   * option rows align vertically with the sibling groups that do.
   */
  reservePreview?: boolean
  /**
   * Grey out and lock the whole group — e.g. the mesh picker when a
   * marine-grade grille (integral mesh) is selected. `disabledNote` explains
   * why so the greyed group isn't mysterious.
   */
  disabled?: boolean
  disabledNote?: string
}) {
  // Placeholder rows SAP pads these lists with can't be rendered — same
  // filter the group-visibility and completeness checks above use, so what's
  // required is always something the customer can actually see and click.
  const visible = pickable(options)
  const active = value !== '' ? (visible.find((o) => o.ProdType === value) ?? null) : null
  // Preview strictly follows the selection — no image until an option with
  // one is actually checked. Sub-options never use characteristic images
  // (legacy passed "false").
  const previewSrc = active?.Image ? getScreenImageSrc(active.Image, false) : undefined
  return (
    <fieldset className={`al-cfg__group ${disabled ? 'is-disabled' : ''}`} disabled={disabled}>
      <legend className="al-cfg__group-label xxsmall uppercase dark-grey">
        {title}
        {disabled && disabledNote ? (
          <span className="al-cfg__group-note xxsmall dark-grey"> — {disabledNote}</span>
        ) : null}
      </legend>
      <div className={`al-cfg__group-body ${previewSrc || reservePreview ? 'has-preview' : ''}`}>
        <div className="al-cfg__options">
          {visible.map((o) => {
            // A sub-option's charged price is Price + NonTradeCharge — the
            // same two fields lineTotal() adds (meshPrice + meshNonTrade,
            // etc.) and the SAP payload sends. Showing Price alone under-
            // reports the pill whenever NonTradeCharge is nonzero, the same
            // way the screen-type card once dropped FitCost/NonTradeCharge.
            const price = parseFloat(o.Price || '0') + parseFloat(o.NonTradeCharge || '0')
            const isActive = value === o.ProdType
            return (
              <label key={o.ProdType} className={`al-cfg__pill ${isActive ? 'is-active' : ''}`}>
                <input
                  type="radio"
                  name={name}
                  value={o.ProdType}
                  checked={isActive}
                  onChange={() => onChange(o.ProdType)}
                  disabled={disabled}
                />
                {/* Visible radio circle — the outline alone wasn't a clear
                    enough "selected" signal. Mirrors the screen-type card's
                    radio so both levels of choice read the same way. */}
                <span className="al-cfg__pill-radio" aria-hidden="true" />
                <span className="al-cfg__pill-label">
                  <span className="xsmall">{o.Desc}</span>
                  {price > 0 ? (
                    <span className="al-cfg__pill-price xxsmall accent">+${price.toFixed(2)}</span>
                  ) : null}
                </span>
                <InfoTip text={o.InfoText} label={`About ${o.Desc}`} className="m-lx2" />
              </label>
            )
          })}
        </div>
        {previewSrc ? (
          <figure className="al-cfg__preview no-m">
            <img src={previewSrc} alt="" />
            <figcaption className="xxsmall dark-grey text-center">{active?.Desc}</figcaption>
          </figure>
        ) : null}
      </div>
    </fieldset>
  )
}
